import {Stack, Space, List} from '@mantine/core';
import {PlainTextCodeBox} from "./PlainTextCodeBox";
import {Overview} from "./Overview";
import {NormalText} from "./NormalText";
import {PageTitleText} from "./PageTitleText";
import {PageDescriptionText} from "./PageDescriptionText";
import {CommandNameText} from "./CommandNameText";
import {CommandAbbreviationText} from "./CommandAbbreviationText";
import {CodeBox} from "./CodeBox";

const inputs = [
    {input: "genome_data.fastq.gz"},
    {input: "SRR1513231"},
    {input: "SRR1513231,DRR259113"},
    {input: "list_of_accession_numbers.txt"},
    {input: "SRR1513231 --kmer 8"},
    {input: "SRR1513231 --output-dir C:\\Users\\...\\Desktop\\output"},
    {input: "SRR1513231 --thead-count 24"},
    {input: "--help"}
]

const list = `
SRR1513231
DRR259113
ERR4470013
`;

const codeTexts = inputs.map((input) => {
    return {...input, command: "lagg", subCommand: "jellyfish"};
})

export function JellyfishTextSection() {
    return (
        <Stack
            justify="flex-start"
            align="flex-start"
            gap="xl"
            mr="xl"
            ml="xl"
            mb="xl"
            maw="60rem"
        >
            <Stack
                gap="xs"
                justify="flex-start">

                <Overview/>

                <PageTitleText>
                    jellyfish
                </PageTitleText>

                <PageDescriptionText>
                    This command is used for converting FASTQ files.
                </PageDescriptionText>

                <Space h="sm"/>

                <NormalText>
                    The jellyfish command is the primary tool for converting FASTQ files into k-mer counted formats.
                    It allows users to specify k-mer values, multi-threading, and output paths for efficient data
                    processing and analysis.
                </NormalText>
            </Stack>

            <Stack gap={'m'} id='input'>
                <CommandNameText>
                    Input Argument
                </CommandNameText>

                <NormalText>
                    A required argument that can go before or after the option flags.
                    <Space h="xs"/>
                    For this command, the input options must be one of the following:
                    <List>
                        <List.Item>A path to a .fastq or .fastq.gz file.</List.Item>
                        <List.Item>An SRA run accession number.</List.Item>
                        <List.Item>A path to a text file containing line-separated SRA run accession
                            numbers.</List.Item>
                    </List>
                    <Space h="xs"/>
                    To use multiple inputs, provide a comma-separated list of any of the above.
                </NormalText>
                <Space h="sm"/>
                <NormalText>
                    The example below uses a .fastq.gz file to count 10-mers (by default):
                </NormalText>
                <CodeBox codeText={codeTexts[0]}/>

                <NormalText>
                    This example below uses a single run accession number to count 10-mers:
                </NormalText>
                <CodeBox codeText={codeTexts[1]}/>

                <NormalText>
                    To use multiple accession numbers, simply comma-separate them:
                </NormalText>
                <CodeBox codeText={codeTexts[2]}/>

                <NormalText>
                    You can also use a list to a text file of line-separated run accession numbers like so:
                </NormalText>
                <CodeBox codeText={codeTexts[3]}/>

                <NormalText>
                    list_of_accession_numbers.txt:
                </NormalText>
                <PlainTextCodeBox>{list}</PlainTextCodeBox>
            </Stack>

            <Stack gap={'m'} id='kvalue'>
                <CommandNameText>
                    --kmer <CommandAbbreviationText> (-k)</CommandAbbreviationText>
                </CommandNameText>

                <NormalText>
                    Specify your desired k-mer length for counting.
                </NormalText>

                <NormalText>
                    Will convert the data specified by the accession number SRR1513231 to a k-mer
                    format with a k-count of 8.
                </NormalText>
                <CodeBox codeText={codeTexts[4]}/>
            </Stack>

            <Stack gap={'m'} id="output-path">
                <CommandNameText>
                    --output-dir <CommandAbbreviationText> (-o)</CommandAbbreviationText>
                </CommandNameText>

                <NormalText>
                    Used if want to change the output location of the outputted file. The default location is the same
                    directory as the terminal.
                </NormalText>

                <NormalText>
                    Will output the converted data specified by the accession number SRR1513231 to a folder named
                    output on the user's Desktop.
                </NormalText>
                <CodeBox codeText={codeTexts[5]}/>
            </Stack>

            <Stack gap={'m'} id='thread-count'>
                <CommandNameText>
                    --thread-count <CommandAbbreviationText> (-t)</CommandAbbreviationText>
                </CommandNameText>

                <NormalText>
                    Specify the number of threads to use. The default thread count is 16.
                </NormalText>

                <NormalText>
                    Will convert the data specified by the accession number SRR1513231 to a format with a k-count of 10
                    while using 24
                    threads for the k-mer counting.
                </NormalText>
                <CodeBox codeText={codeTexts[6]}/>
            </Stack>

            <Stack gap={'m'} id="help">
                <CommandNameText>
                    --help
                </CommandNameText>


                <NormalText>
                    Will show the list of command options for jellyfish in the terminal along with default values, input
                    type, and example use cases.
                </NormalText>
                <CodeBox codeText={codeTexts[7]}/>

                <Space h='xs'/>
            </Stack>
        </Stack>
    );
}